import React from 'react';
import { styles } from './AddedToCartAlert.css';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { ProgressiveImage } from '../hooks/useProgressiveImage/useProgressiveImage';
import resizeShopifyImage from '@jbhi-fi/utils/src/resizeShopifyImage/resizeShopifyImage';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { useIdPrefix } from '../IdPrefix';
import PromoTag from '@jbhi-fi/lanyard-ui/components/PromoTag';
import { ProductImageFallback } from '../ProductImageFallback/ProductImageFallback';
import { ActionButton } from '@jbhi-fi/components/src/ActionButton';
import { getCleanHtml } from '@jbhi-fi/utils/src/getCleanHtml';

export type AddedToCartAlertProps = {
  onReviewCartClick: React.MouseEventHandler<HTMLButtonElement>;
  onContinueShoppingClick: React.MouseEventHandler<HTMLButtonElement>;
  productImageSrc?: string;
  productTitle?: string;
  bonusProducts?: any;
  variant?: 'default' | 'minimalist';
  background?: 'success' | 'grey' | 'none';
  isAddedToCart?: boolean;
  shouldPlayAddedAnimation?: boolean;
  shouldShowButtons?: boolean;
  isESimOnly?: boolean;
} & (
  | { variant?: 'default'; onCheckoutClick: React.MouseEventHandler<HTMLButtonElement> }
  | { variant?: 'minimalist'; onCheckoutClick?: () => void }
);
export const AddedToCartAlert = ({
  productImageSrc,
  productTitle,
  onCheckoutClick,
  onReviewCartClick,
  onContinueShoppingClick,
  bonusProducts = [],
  variant = 'default',
  background = 'success',
  isAddedToCart = false,
  shouldPlayAddedAnimation = false,
  shouldShowButtons = true,
  isESimOnly,
}: AddedToCartAlertProps) => {
  const testIdPrefix = useIdPrefix() || '';

  const bonusProductsSection = (
    <div>
      {bonusProducts.map((bonusProduct: any, index: number) => (
        <div key={index} className={styles.bonusProductContent}>
          {bonusProducts.length > 1 && index === 0 && (
            <div>
              <PromoTag size="sm" label="Free Bonus Items" />
            </div>
          )}
          <div className={styles.bonusProductItem({ isSingleItem: bonusProducts.length == 1 })}>
            {bonusProduct.product_image ? (
              <ProgressiveImage
                className={styles.bonusProductImage}
                placeholderSrc={resizeShopifyImage(bonusProduct.product_image, '32x32')}
                src={resizeShopifyImage(bonusProduct.product_image, '240x240')}
                alt={`${bonusProduct.title} image`}
                width={30}
                height={30}
              />
            ) : (
              <ProductImageFallback className={styles.bonusProductImage} />
            )}
            <div className={styles.bonusProductTitle}>
              {bonusProducts.length === 1 && <PromoTag size="sm" label="Free Bonus Item" />}
              <span
                className={styles.productTitle}
                data-testid={`${testIdPrefix ? testIdPrefix : ''}bonus-product-title`}
              >
                {bonusProduct.title}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  if (variant === 'minimalist')
    return (
      <div className={styles.addedToCartAlert({ background })} data-testid={`${testIdPrefix}added-to-cart-alert`}>
        {(shouldPlayAddedAnimation && isAddedToCart) || !shouldPlayAddedAnimation ? (
          <div className={styles.heading}>
            <Icon name="Tick" /> Item added to your cart!
          </div>
        ) : null}
        {shouldShowButtons ? (
          <div className={styles.contents({ variant })}>
            <Button onClick={onContinueShoppingClick} type="link" label="Continue shopping" />
            <div className={styles.buttonContainer}>
              <ActionButton onClick={onReviewCartClick} type="primary" label="Review cart" />
            </div>
          </div>
        ) : null}
      </div>
    );

  return (
    <div className={styles.addedToCartAlert({ background })} data-testid={`${testIdPrefix}added-to-cart-alert`}>
      <h5 className={styles.heading} data-testid={`${testIdPrefix}added-to-cart-alert-heading`}>
        <Icon name="Tick" /> Item added to your cart!
      </h5>
      <div className={styles.contents({ variant })}>
        <div>
          <div className={styles.product}>
            {productImageSrc ? (
              <ProgressiveImage
                className={styles.productImage}
                placeholderSrc={resizeShopifyImage(productImageSrc, '32x32')}
                src={resizeShopifyImage(productImageSrc, '240x240')}
                alt={`${productTitle} image`}
                width={80}
                height={80}
                data-testid={`${testIdPrefix}added-to-cart-alert-image`}
              />
            ) : (
              <ProductImageFallback className={styles.productImage} />
            )}
            <p className={styles.productTitle} data-testid={`${testIdPrefix}product-title`}>
              {productTitle}
            </p>
          </div>
          {bonusProducts?.length ? bonusProductsSection : null}
        </div>
        {shouldShowButtons ? (
          <div className={styles.actions}>
            <Button
              type="primary"
              classes={styles.secondaryActionButton}
              label="Review cart"
              onClick={onReviewCartClick}
              data-testid={`${testIdPrefix}review-button`}
            />
            <Button
              type="action"
              classes={styles.primaryActionButton}
              label="Checkout"
              onClick={onCheckoutClick!}
              data-testid={`${testIdPrefix}checkout-button`}
            />
          </div>
        ) : null}
      </div>

      {isESimOnly ? (
        <div className={styles.eSimMessage}>
          <h4>{window.Jbhifi.eSimMessagingTitle ? window.Jbhifi.eSimMessagingTitle : 'This device is eSim only!'}</h4>

          {window.Jbhifi.eSimMessagingContent.length > 0 ? (
            <p
              dangerouslySetInnerHTML={{
                __html: getCleanHtml(window.Jbhifi.eSimMessagingContent, ['b', 'strong', 'a', 'br', 'i', 'em']),
              }}
            ></p>
          ) : (
            <p>Please ensure your mobile provider supports eSim.</p>
          )}
        </div>
      ) : null}
    </div>
  );
};
